// PostgREST-Count-Query-Ketten sind bewusst je Zweck ausgeschrieben (Badge-Vertraege);
// die Zwillinge (open/overdue je Entitaet) sind kein extrahierbarer Mehrwert (Repo-Muster,
// vgl. useTasksQuery).
// fallow-ignore-file code-duplication
/**
 * Consolidated Open Counts Hooks
 *
 * This module provides TanStack Query-based hooks for counting open/overdue
 * inspections, defects, and tasks. Uses the centralized RealtimeProvider
 * for efficient real-time updates with debouncing.
 *
 * DESIGN-UNTERSCHIED (dokumentiert, TES-1995 — bitte nicht erneut als Bug melden):
 * Die Sidebar-Badges hier zählen bewusst RLS-WEIT ("system-wide": alles, was der
 * User sehen darf). Die Dashboard-Kacheln sind für Nicht-QM dagegen USER-SCOPED
 * (dashboard-metrics/index.ts filtert auf assigned/created) — Badge und Kachel
 * dürfen deshalb für Nicht-QM verschieden sein. Für QM sind beide RLS-weit und
 * müssen übereinstimmen (E2E-Assert in counter-updates.spec).
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { getTodayStartISO } from "@/lib/dateUtils";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";

// Query key factory for open counts - includes tenantId for proper cache isolation
export const openCountsKeys = {
  all: ["openCounts"] as const,
  inspections: (tenantId: string | null) =>
    [...openCountsKeys.all, "inspections", tenantId] as const,
  defects: (tenantId: string | null) => [...openCountsKeys.all, "defects", tenantId] as const,
  // TES-2287 (Muster tasksKeys.list, TES-2058): isQmAdmin geht mit in den Key — das Rollen-Flag
  // löst ASYNCHRON auf (useAuth fetchRoles); ohne Key-Anteil bliebe ein Cache-Hit von VOR der
  // Rollen-Auflösung (isQmAdmin noch false → user-gescopter Count) als Badge stehen. OHNE Flag
  // bleibt der Key bewusst ein PRÄFIX beider Rollen-Varianten (kein explizites undefined-Element),
  // damit die invalidateOpenCounts-Filter weiterhin alle Varianten treffen.
  tasks: (userId: string, tenantId: string | null, isQmAdmin?: boolean) =>
    isQmAdmin === undefined
      ? ([...openCountsKeys.all, "tasks", userId, tenantId] as const)
      : ([...openCountsKeys.all, "tasks", userId, tenantId, isQmAdmin] as const),
};

interface OpenCountResult {
  openCount: number;
  overdueCount: number;
}

interface TaskCountResult {
  count: number;
  overdueCount: number;
}

// TES-2053: types.ts ist gegenüber Mig 381 stale — schmaler RPC-Client-Typ (Repo-Muster,
// vgl. useDefectReopen). Head-Count-Variante: nur der Zähler wird gelesen.
type MyInspectionsToVerifyCountRpc = {
  rpc: (
    fn: "my_inspections_to_verify",
    args: Record<string, never>,
    options: { count: "exact"; head: true },
  ) => PromiseLike<{ count: number | null; error: { message: string } | null }>;
};

// ============ Fetch Functions ============

async function fetchInspectionCounts(): Promise<OpenCountResult> {
  const todayStart = getTodayStartISO();

  // Count ALL open inspections (pending + in_progress)
  const { count: openInspections, error: openError } = await supabase
    .from("inspections")
    .select("id", { count: "exact", head: true })
    .in("status", ["pending", "in_progress"]);

  if (openError) throw openError;

  // Count ALL overdue inspections
  const { count: overdueInspections, error: overdueError } = await supabase
    .from("inspections")
    .select("id", { count: "exact", head: true })
    .in("status", ["pending", "in_progress"])
    .lt("due_date", todayStart);

  if (overdueError) throw overdueError;

  return {
    openCount: openInspections || 0,
    overdueCount: overdueInspections || 0,
  };
}

async function fetchDefectCounts(): Promise<OpenCountResult> {
  const todayStart = getTodayStartISO();

  // Count ALL open defects (open, in_progress, resolved).
  // TES-1995: Top-Level only (`parent_defect_id IS NULL`) — die Mängel-Liste blendet
  // Unteraufgaben/Maßnahmen aus (fetchDefects, MGM-18); ohne denselben Filter zählte
  // das Badge um die Subtasks höher als die Liste.
  const { count: openDefects, error: openError } = await supabase
    .from("defects")
    .select("id", { count: "exact", head: true })
    .is("deleted_at", null)
    .is("parent_defect_id", null)
    .in("status", ["open", "in_progress", "resolved"]);

  if (openError) throw openError;

  // Count ALL overdue defects (Top-Level only, s.o.)
  const { count: overdueDefects, error: overdueError } = await supabase
    .from("defects")
    .select("id", { count: "exact", head: true })
    .is("deleted_at", null)
    .is("parent_defect_id", null)
    .in("status", ["open", "in_progress", "resolved"])
    .lt("due_date", todayStart);

  if (overdueError) throw overdueError;

  return {
    openCount: openDefects || 0,
    overdueCount: overdueDefects || 0,
  };
}

// TES-2287 (Badge=Seite, Follow-up zu TES-2058 [R12]): qm_admin (Org-Hülle, team-/gruppenlos)
// sieht auf der Aufgaben-Seite TENANT-WEIT alle "braucht Aktion"-Mängel/Prüfungen (Oversight-
// Zweig in useTasksQuery/fetchTasks, Mig 411). Das Nav-Badge zählt dieselbe Grundmenge:
// gleicher Status-Cut wie die Oversight-Queries der Seite (defects open/in_progress/resolved,
// inspections pending/in_progress). Die assigned_to-/verifying_qm-Zweige von fetchTaskCounts
// sind STRIKTE TEILMENGEN dieser tenant-weiten Zählung (gleiche Status-Cuts) und entfallen hier
// ersatzlos — die tenant-weite Zählung IST das deduplizierte Ergebnis (die Seite filtert die
// Oversight-Zeilen gegen die eigenen Buckets, die Vereinigung bleibt die tenant-weite Menge).
// Die auf der Seite als Extra-Typen ausgewiesenen Queues (unassigned created_by, Gruppen-Queue)
// zählen dort NICHT in die Primär-Zähler und sind für qm_admin strukturell leer (gruppenlos,
// read-only/nicht operativ) — keine Subtraktion nötig.
async function fetchQmAdminOversightTaskCounts(): Promise<TaskCountResult> {
  const todayStart = getTodayStartISO();

  // Fünf unabhängige Head-Counts parallel (Copilot PR #861; Muster useTasksQuery Promise.all) —
  // das Shell-Badge soll nicht die Summe von fünf Roundtrip-Latenzen abwarten:
  // 1. Tenant-weite offene Mängel (open/in_progress/resolved) — Spiegel der Oversight-Query der Seite.
  // 2. Tenant-weite offene Prüfungen (pending/in_progress) — Spiegel der Oversight-Query der Seite.
  // 3. TES-2053-Muster: zu-verifizierende Prüfungen (Caller = Verifizierer/Deputy) zählen additiv —
  //    sie sind completed und liegen damit AUSSERHALB der pending/in_progress-Oversight-Menge
  //    (kein Doppelzählen). Gleiche Quelle wie die Seite (RPC my_inspections_to_verify, Mig 381).
  // 4./5. Tenant-weite überfällige Mängel/Prüfungen (gleicher due_date-Cut wie der user-gescopte Zweig).
  const [
    { count: oversightDefects, error: oversightDefError },
    { count: oversightInspections, error: oversightInspError },
    { count: inspectionVerifications, error: inspVerifyError },
    { count: overdueDefects, error: overdueDefError },
    { count: overdueInspections, error: overdueInspError },
  ] = await Promise.all([
    supabase
      .from("defects")
      .select("id", { count: "exact", head: true })
      .is("deleted_at", null)
      .in("status", ["open", "in_progress", "resolved"]),
    supabase
      .from("inspections")
      .select("id", { count: "exact", head: true })
      .in("status", ["pending", "in_progress"]),
    (supabase as unknown as MyInspectionsToVerifyCountRpc).rpc(
      "my_inspections_to_verify",
      {},
      { count: "exact", head: true },
    ),
    supabase
      .from("defects")
      .select("id", { count: "exact", head: true })
      .is("deleted_at", null)
      .in("status", ["open", "in_progress", "resolved"])
      .lt("due_date", todayStart),
    supabase
      .from("inspections")
      .select("id", { count: "exact", head: true })
      .in("status", ["pending", "in_progress"])
      .lt("due_date", todayStart),
  ]);

  if (oversightDefError) throw oversightDefError;
  if (oversightInspError) throw oversightInspError;
  if (inspVerifyError) throw inspVerifyError;
  if (overdueDefError) throw overdueDefError;
  if (overdueInspError) throw overdueInspError;

  return {
    count: (oversightDefects || 0) + (oversightInspections || 0) + (inspectionVerifications || 0),
    // TES-2053: zu-verifizierende (completed) Prüfungen zählen NIE als überfällig (Badge = Seite).
    overdueCount: (overdueDefects || 0) + (overdueInspections || 0),
  };
}

async function fetchTaskCounts(userId: string): Promise<TaskCountResult> {
  const todayStart = getTodayStartISO();

  // Count pending + in_progress inspections assigned to the current user
  const { count: activeInspections, error: inspError } = await supabase
    .from("inspections")
    .select("id", { count: "exact", head: true })
    .eq("assigned_to", userId)
    .in("status", ["pending", "in_progress"]);

  if (inspError) throw inspError;

  // Count open + in_progress + resolved defects assigned to the current user
  // Note: resolved defects where user is assigned_to are still "open tasks" for the user
  const { count: activeDefects, error: defError } = await supabase
    .from("defects")
    .select("id", { count: "exact", head: true })
    .is("deleted_at", null)
    .eq("assigned_to", userId)
    .in("status", ["open", "in_progress", "resolved"]);

  if (defError) throw defError;

  // Count resolved defects awaiting verification (where user is verifying_qm but NOT assigned_to)
  // This prevents double-counting when user is both assigned_to and verifying_qm
  const { count: pendingVerifications, error: verifyError } = await supabase
    .from("defects")
    .select("id", { count: "exact", head: true })
    .is("deleted_at", null)
    .eq("verifying_qm", userId)
    .neq("assigned_to", userId)
    .eq("status", "resolved");

  if (verifyError) throw verifyError;

  // TES-2053 (Badge-Parität, TES-1994-Muster): zu-verifizierende Prüfungen (Caller = aufgelöster
  // Verifizierer/Deputy) zählen als Aufgabe mit — gleiche Quelle wie die Aufgaben-Seite
  // (SECURITY-DEFINER-RPC my_inspections_to_verify, Mig 381). Kein Doppelzählen mit
  // activeInspections: die zählen nur pending/in_progress, zu-verifizieren ist completed.
  const { count: inspectionVerifications, error: inspVerifyError } = await (
    supabase as unknown as MyInspectionsToVerifyCountRpc
  ).rpc("my_inspections_to_verify", {}, { count: "exact", head: true });

  if (inspVerifyError) throw inspVerifyError;

  // Count overdue inspections
  const { count: overdueInspections, error: overdueInspError } = await supabase
    .from("inspections")
    .select("id", { count: "exact", head: true })
    .eq("assigned_to", userId)
    .in("status", ["pending", "in_progress"])
    .lt("due_date", todayStart);

  if (overdueInspError) throw overdueInspError;

  // Count overdue defects (assigned) - includes resolved status
  const { count: overdueDefects, error: overdueDefError } = await supabase
    .from("defects")
    .select("id", { count: "exact", head: true })
    .is("deleted_at", null)
    .eq("assigned_to", userId)
    .in("status", ["open", "in_progress", "resolved"])
    .lt("due_date", todayStart);

  if (overdueDefError) throw overdueDefError;

  // Count overdue verifications (where user is verifying_qm but NOT assigned_to)
  const { count: overdueVerifications, error: overdueVerifyError } = await supabase
    .from("defects")
    .select("id", { count: "exact", head: true })
    .is("deleted_at", null)
    .eq("verifying_qm", userId)
    .neq("assigned_to", userId)
    .eq("status", "resolved")
    .lt("due_date", todayStart);

  if (overdueVerifyError) throw overdueVerifyError;

  return {
    count:
      (activeInspections || 0) +
      (activeDefects || 0) +
      (pendingVerifications || 0) +
      (inspectionVerifications || 0),
    // TES-2053: zu-verifizierende Prüfungen fließen NICHT in overdueCount — completed-Prüfungen
    // gelten seitenseitig nie als überfällig (isInspectionOverdue, Badge = Seite).
    overdueCount: (overdueInspections || 0) + (overdueDefects || 0) + (overdueVerifications || 0),
  };
}

// ============ Hooks ============

/**
 * Hook to count ALL open inspections (system-wide, based on RLS permissions)
 * and separately count how many of those are overdue.
 *
 * Open = pending + in_progress
 * Overdue = open inspections where due_date < now
 */
export function useOpenInspections() {
  const { user, profile } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = profile?.active_tenant_id ?? null;

  const query = useQuery({
    queryKey: openCountsKeys.inspections(tenantId),
    queryFn: fetchInspectionCounts,
    enabled: !!user && !!tenantId,
  });

  // Use centralized realtime subscription with debouncing
  useRealtimeSubscription(
    "inspections",
    () => queryClient.invalidateQueries({ queryKey: openCountsKeys.inspections(tenantId) }),
    { enabled: !!user && !!tenantId },
  );

  return {
    openCount: query.data?.openCount ?? 0,
    overdueCount: query.data?.overdueCount ?? 0,
    loading: query.isLoading,
  };
}

/**
 * Hook to count ALL open defects (system-wide, based on RLS permissions)
 * and separately count how many of those are overdue.
 *
 * Open = open + in_progress + resolved (pending verification)
 * Overdue = open defects where due_date < now
 */
export function useOpenDefects() {
  const { user, profile } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = profile?.active_tenant_id ?? null;

  const query = useQuery({
    queryKey: openCountsKeys.defects(tenantId),
    queryFn: fetchDefectCounts,
    enabled: !!user && !!tenantId,
  });

  // Use centralized realtime subscription with debouncing
  useRealtimeSubscription(
    "defects",
    () => queryClient.invalidateQueries({ queryKey: openCountsKeys.defects(tenantId) }),
    { enabled: !!user && !!tenantId },
  );

  return {
    openCount: query.data?.openCount ?? 0,
    overdueCount: query.data?.overdueCount ?? 0,
    loading: query.isLoading,
  };
}

/**
 * Hook to count all open tasks assigned to the current user:
 * - Pending/In-Progress inspections (assigned to user)
 * - Open/In-Progress defects (assigned to user)
 * - Resolved defects awaiting verification (where user is verifying_qm)
 * - Completed inspections awaiting the caller's verification (verifier/deputy, TES-2053)
 *
 * qm_admin (TES-2287, Follow-up zu TES-2058): tenant-weite Oversight-Zählung statt der
 * user-gescopten Zweige — Parität zur Aufgaben-Seite (useTasksQuery isQmAdmin-Zweig).
 */
export function useOpenTasks() {
  const { user, profile, isQmAdmin } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = profile?.active_tenant_id ?? null;

  const query = useQuery({
    queryKey: openCountsKeys.tasks(user?.id ?? "", tenantId, isQmAdmin),
    // TES-2287: qm_admin zählt tenant-weit (Oversight-Parität zur Aufgaben-Seite); der
    // user-gescopte Zweig (fetchTaskCounts) bleibt byte-identisch.
    queryFn: () => (isQmAdmin ? fetchQmAdminOversightTaskCounts() : fetchTaskCounts(user!.id)),
    enabled: !!user && !!tenantId,
  });

  // Use centralized realtime subscription with debouncing
  // Subscribe to both inspections and defects
  // TES-2287: bewusst OHNE isQmAdmin-Flag — der Präfix-Key invalidiert beide Rollen-Varianten.
  useRealtimeSubscription(
    ["inspections", "defects"],
    () =>
      queryClient.invalidateQueries({ queryKey: openCountsKeys.tasks(user?.id ?? "", tenantId) }),
    { enabled: !!user && !!tenantId },
  );

  return {
    count: query.data?.count ?? 0,
    overdueCount: query.data?.overdueCount ?? 0,
    loading: query.isLoading,
  };
}
